import type { GetProjectsAffectedByChangeBffResponse, ProjectResource, ModifyTenantCommonVariablesCommand, TenantVariableResource, ModifyTenantCommonVariableValue } from "@octopusdeploy/octopus-server-client";
import { useState } from "react";
import AffectedProjectsNotificationDialog from "~/areas/projects/components/Variables/TenantVariables/AffectedProjectsNotificationDialog";
import CommonTemplatesInlineContent from "~/areas/projects/components/Variables/TenantVariables/CommonTemplatesInlineContent";
import ScopedCommonTemplatesInlineContent from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/ScopedCommonTemplatesInlineContent";
import type { ScopedCommonTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedCommonTemplatesState";
import { useScopedCommonTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/ScopedTenantVariables/useScopedCommonTemplatesState";
import SharedTenantVariablesPageContent, { type TenantVariablesPageSaveAction } from "~/areas/projects/components/Variables/TenantVariables/SharedTenantVariablesPageContent";
import type { ChangedCommonTemplate, CommonTemplatesChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import { useCommonTemplatesChangeTracker } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesChangeTracker";
import type { CommonTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesState";
import { useCommonTemplatesState } from "~/areas/projects/components/Variables/TenantVariables/useCommonTemplatesState";
import { useTenantVariableFiltersData } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableFiltersData";
import { useTenantVariableResourceOptions } from "~/areas/projects/components/Variables/TenantVariables/useTenantVariableResourceOptions";
import { repository } from "~/clientInstance";
import { useLegacyDoBusyTask } from "~/components/DataBaseComponent/useLegacyDoBusyTask";
import Dialog from "~/components/Dialog/Dialog";
import { useDialogTrigger } from "~/components/Dialog/DialogTrigger";
import { isFeatureToggleEnabled } from "~/components/FeatureToggle/New/FeatureToggleContext";
interface CommonTemplatesPageProps {
    project: ProjectResource;
}
function ScopedCommonTemplatesPage({ project }: CommonTemplatesPageProps) {
    const { status: legacyStatus, doBusyTask } = useLegacyDoBusyTask();
    const scopedContentState = useScopedCommonTemplatesState(project, doBusyTask);
    const filterData = useTenantVariableFiltersData(project.Id, doBusyTask);
    const variableResourceOptions = useTenantVariableResourceOptions(doBusyTask);
    const changeTracker = useCommonTemplatesChangeTracker();
    const { isOpen, openDialog } = useDialogTrigger();
    const [affectedProjects, setAffectedProjects] = useState<GetProjectsAffectedByChangeBffResponse | null>(null);
    const saveAction: TenantVariablesPageSaveAction = {
        onClick: async () => {
            await doBusyTask(async () => {
                const response = await getAffectedProjects(project, changeTracker);
                setAffectedProjects(response);
                if (response && response.TotalCount > 0) {
                    openDialog();
                }
                else {
                    await saveScoped(changeTracker, scopedContentState);
                }
            });
        },
        disabled: !changeTracker.hasAny(),
    };
    const saveByConfirmation = async () => await doBusyTask(async () => {
        await saveScoped(changeTracker, scopedContentState);
        setAffectedProjects(null);
    });
    const onConfirmationCancelled = () => setAffectedProjects(null);
    return (<SharedTenantVariablesPageContent legacyStatus={legacyStatus} project={project} doBusyTask={doBusyTask} saveAction={saveAction} warnings={scopedContentState.warnings} page={"common"}>
            {project ? <ScopedCommonTemplatesInlineContent project={project} doBusyTask={doBusyTask} contentState={scopedContentState} filterData={filterData} variableResourceOptions={variableResourceOptions} changeTracker={changeTracker}/> : null}
            <Dialog open={isOpen}>
                <AffectedProjectsNotificationDialog affectedProjects={affectedProjects} onSave={saveByConfirmation} onCancel={onConfirmationCancelled}></AffectedProjectsNotificationDialog>
            </Dialog>
        </SharedTenantVariablesPageContent>);
}
function UnscopedCommonTemplatesPage({ project }: CommonTemplatesPageProps) {
    const { status: legacyStatus, doBusyTask } = useLegacyDoBusyTask();
    const contentState = useCommonTemplatesState(project, doBusyTask);
    const filterData = useTenantVariableFiltersData(project.Id, doBusyTask);
    const variableResourceOptions = useTenantVariableResourceOptions(doBusyTask);
    const changeTracker = useCommonTemplatesChangeTracker();
    const { isOpen, openDialog } = useDialogTrigger();
    const [affectedProjects, setAffectedProjects] = useState<GetProjectsAffectedByChangeBffResponse | null>(null);
    const saveAction: TenantVariablesPageSaveAction = {
        onClick: async () => {
            await doBusyTask(async () => {
                const response = await getAffectedProjects(project, changeTracker);
                setAffectedProjects(response);
                if (response && response.TotalCount > 0) {
                    openDialog();
                }
                else {
                    await save(changeTracker, contentState);
                }
            });
        },
        disabled: !changeTracker.hasAny(),
    };
    const saveByConfirmation = async () => await doBusyTask(async () => {
        await save(changeTracker, contentState);
        setAffectedProjects(null);
    });
    const onConfirmationCancelled = () => setAffectedProjects(null);
    return (<SharedTenantVariablesPageContent legacyStatus={legacyStatus} project={project} doBusyTask={doBusyTask} saveAction={saveAction} warnings={contentState.warnings} page={"common"}>
            {project ? <CommonTemplatesInlineContent project={project} doBusyTask={doBusyTask} contentState={contentState} filterData={filterData} variableResourceOptions={variableResourceOptions} changeTracker={changeTracker}/> : null}
            <Dialog open={isOpen}>
                <AffectedProjectsNotificationDialog affectedProjects={affectedProjects} onSave={saveByConfirmation} onCancel={onConfirmationCancelled}></AffectedProjectsNotificationDialog>
            </Dialog>
        </SharedTenantVariablesPageContent>);
}
export default function TenantVariablesCommonTemplatesPage({ project }: CommonTemplatesPageProps) {
    return isFeatureToggleEnabled("CommonVariableScopingFeatureToggle") ? <ScopedCommonTemplatesPage project={project}/> : <UnscopedCommonTemplatesPage project={project}/>;
}
async function getAffectedProjects(project: ProjectResource, changeTracker: CommonTemplatesChangeTracker) {
    const changedSets = changeTracker.values().map((v) => ({ SetId: v.setId, TenantId: v.tenantId }));
    return await repository.Projects.getProjectsAffectedByCommonTemplateChange(project.Id, changedSets);
}
async function save(changeTracker: CommonTemplatesChangeTracker, contentState: CommonTemplatesState) {
    await saveChangedValues(changeTracker);
    await contentState.actions.onSave();
    changeTracker.reset();
}
async function saveScoped(changeTracker: CommonTemplatesChangeTracker, scopedContentState: ScopedCommonTemplatesState) {
    await saveCommonTemplates(changeTracker);
    await scopedContentState.actions.onSave();
    changeTracker.reset();
}
async function saveCommonTemplates(changeTracker: CommonTemplatesChangeTracker) {
    const changedTenants = changeTracker.changedTenants();
    const tenantCommonVariablesPromises = Array.from(changedTenants).map((id) => repository.Tenants.getCommonVariablesByTenantId(id));
    const tenantCommonVariables = await Promise.all(tenantCommonVariablesPromises);
    const tenantVariablesLookup: {
        [tenantId: string]: ModifyTenantCommonVariablesCommand;
    } = {};
    for (const tenantCommonVariable of tenantCommonVariables) {
        const { TenantId, Variables } = tenantCommonVariable;
        const variables = Variables.map((v) => ({ Id: v.Id, LibraryVariableSetId: v.LibraryVariableSetId, TemplateId: v.TemplateId, Template: v.Template, Value: v.Value, Scope: v.Scope }));
        tenantVariablesLookup[tenantCommonVariable.TenantId] = { TenantId: TenantId, Variables: variables };
    }
    const modifiedTenantVariables = modifyCommonTenantVariables(tenantVariablesLookup, changeTracker.values());
    const savingPromises = modifiedTenantVariables.map((variables) => repository.Tenants.setCommonVariablesByTenantId(variables.TenantId, variables));
    await Promise.all(savingPromises);
}
async function saveChangedValues(changeTracker: CommonTemplatesChangeTracker) {
    const changedTenants = changeTracker.changedTenants();
    const tenantVariablesPromises = Array.from(changedTenants).map((id) => repository.Tenants.getVariablesByTenantId(id));
    const tenantVariables = await Promise.all(tenantVariablesPromises);
    const tenantVariablesLookup: {
        [tenantId: string]: TenantVariableResource;
    } = {};
    for (const tenantVariable of tenantVariables) {
        tenantVariablesLookup[tenantVariable.TenantId] = tenantVariable;
    }
    const modifiedTenantVariables = modifyTenantVariables(tenantVariablesLookup, changeTracker.values());
    const savingPromises = modifiedTenantVariables.map((variables) => repository.Tenants.setVariablesByTenantId(variables.TenantId, variables));
    await Promise.all(savingPromises);
}
function modifyTenantVariables(tenantVariables: {
    [tenantId: string]: TenantVariableResource;
}, changes: ChangedCommonTemplate[]): TenantVariableResource[] {
    const modified = new Map<string, TenantVariableResource>();
    for (const { setId, templateId, tenantId, value } of changes) {
        const variables = tenantVariables[tenantId];
        variables.LibraryVariables[setId].Variables[templateId] = value ?? "";
        modified.set(tenantId, variables);
    }
    return Array.from(modified.values());
}
function modifyCommonTenantVariables(tenantVariables: {
    [tenantId: string]: ModifyTenantCommonVariablesCommand;
}, changes: ChangedCommonTemplate[]): ModifyTenantCommonVariablesCommand[] {
    const modified = new Map<string, ModifyTenantCommonVariablesCommand>();
    for (const { id, setId, templateId, tenantId, value, environmentIds } of changes) {
        const variables = tenantVariables[tenantId];
        // Deletion: both value undefined AND environmentIds empty
        if (value === undefined && environmentIds.length === 0) {
            variables.Variables = variables.Variables.filter((v) => v.Id !== id);
            modified.set(tenantId, variables);
            continue;
        }
        const variable = variables.Variables.find((v) => (id && v.Id === id) || (!id && matchVariableByTemplate(v, setId, templateId)));
        if (!variable) {
            // NEW variable - create with both value and scope
            variables.Variables.push({
                Id: id,
                LibraryVariableSetId: setId,
                TemplateId: templateId,
                Value: value ?? "",
                Scope: { EnvironmentIds: environmentIds },
            });
            modified.set(tenantId, variables);
            continue;
        }
        // EXISTING variable - update both value and scope
        variable.Value = value ?? "";
        variable.Scope.EnvironmentIds = environmentIds;
        modified.set(tenantId, variables);
    }
    return Array.from(modified.values());
}
function matchVariableByTemplate(variable: ModifyTenantCommonVariableValue, setId: string, templateId: string): boolean {
    return !variable.Id && variable.LibraryVariableSetId === setId && variable.TemplateId === templateId;
}
